import React, {PropsWithChildren, useContext, useEffect, useState} from 'react'
import {XpertKnowledgeCommentService} from '../../services/XpertKnowledgeComment'
import {XpertKnowledgeCommentDto} from '../../services/XpertKnowledgeComment/type'

interface XpertKnowledgeCommentContext {
  comments: XpertKnowledgeCommentDto[]
  loading: boolean
  createComment: (xpertKnowledgeId: string, comment: string) => Promise<void>
  deleteComment: (xpertKnowledgeId: string, commentId: number) => Promise<void>
  updateComment: (xpertKnowledgeId: string, commentId: number, comment: string) => Promise<void>
}

const Context = React.createContext<XpertKnowledgeCommentContext>(null as any)

interface XpertKnowledgeCommentContextProviderProps {
  xpertKnowledgeId: string
}

export const XpertKnowledgeCommentContextProvider: React.FC<PropsWithChildren<XpertKnowledgeCommentContextProviderProps>> = ({
  children,
  xpertKnowledgeId,
}) => {
  const [comments, setComments] = useState<XpertKnowledgeCommentDto[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const fetchXpertKnowledgeComments = async () => {
    setLoading(true)
    try {
      const fetchedComments = await XpertKnowledgeCommentService.getAll(xpertKnowledgeId)
      setComments(fetchedComments)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (xpertKnowledgeId) {
      fetchXpertKnowledgeComments()
    }
  }, [xpertKnowledgeId])

  useEffect(() => {
    return () => {
      setComments([])
    }
  }, [])

  const createComment = async (xpertKnowledgeId: string, comment: string) => {
    const newComment = await XpertKnowledgeCommentService.create({xpertKnowledgeId, comment})
    setComments((prev) => [...prev, newComment])
  }

  const deleteComment = async (xpertKnowledgeId: string, commentId: number) => {
    await XpertKnowledgeCommentService.remove({xpertKnowledgeId, commentId})
    setComments((prev) => prev.filter((c) => c.id !== commentId))
  }

  const updateComment = async (xpertKnowledgeId: string, commentId: number, comment: string) => {
    const updatedComment = await XpertKnowledgeCommentService.update({xpertKnowledgeId, commentId, comment})
    setComments((prev) => prev.map((c) => (c.id === commentId ? updatedComment : c)))
  }

  return (
    <Context.Provider
      value={{
        comments,
        loading,
        createComment,
        deleteComment,
        updateComment,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useXpertKnowledgeCommentContext = () => useContext(Context)
